import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  alpha,
  Stack,
  Chip,
  Paper,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  Info as InfoIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';
import { AnalysisProvider, useAnalysis } from './context/AnalysisContext';
import { FileUpload } from './components/FileUpload';
import { ResultDisplay } from './components/ResultDisplay';
import { LandingPage } from './components/LandingPage';
import InternalReview from './components/InternalReview';
import PrivacyPolicy from './components/PrivacyPolicy';
import ErrorBoundary from './components/ErrorBoundary';
import { Link as MuiLink } from '@mui/material';
import { SAMPLE_ANALYSIS } from './data/sampleReport';

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      pt: { xs: 7, sm: 8 },
      pb: 4,
    }}
  >
    <Container 
      maxWidth="lg" 
      sx={{
        flex: 1,
        py: 4,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {children}
    </Container>
    
    {/* Footer */}
    <Box 
      component="footer" 
      sx={{
        mt: 'auto',
        py: 3,
        px: 2,
        backgroundColor: (theme) => 
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800],
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Footer removed */}
        </Box>
      </Container>
    </Box>
  </Box>
);



const Navigation = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navItems = [
    { label: 'Home', path: '/', icon: <AssessmentIcon /> },
    { label: 'Analyze', path: '/analyze', icon: <DashboardIcon /> },
    { label: 'How It Works', path: '/how-it-works', icon: <InfoIcon /> },
    { label: 'Contact', path: '/contact', icon: <ContactSupportIcon /> },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <AssessmentIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Typography
              variant="h5"
              component={Link}
              to="/"
              sx={{
                fontWeight: 800,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              PitchGrades
            </Typography>
            <Chip
              label="BETA"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 2, fontSize: '0.7rem', height: 20 }}
            />
          </Box>

          {!isMobile ? (
            <Stack direction="row" spacing={2} alignItems="center">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: isActive(item.path) ? 'primary.main' : 'text.primary',
                    fontWeight: isActive(item.path) ? 600 : 400,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                component={Link}
                to="/analyze"
                sx={{
                  ml: 2,
                  fontWeight: 600,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                Start Analysis
              </Button>
            </Stack>
          ) : (
            <>
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={handleClose}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.icon}
                      {item.label}
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};



const theme = createTheme({
  palette: {
    primary: {
      main: '#0056b3', // IA Dark Blue
      light: '#3378c6',
      dark: '#003b82',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffc72c', // IA Yellow
      light: '#ffd35f',
      dark: '#c79800',
      contrastText: '#000000',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#546e7a',
    },
    success: {
      main: '#28a745',
      light: '#4caf50',
      dark: '#1e7e34',
    },
    warning: {
      main: '#ffc107',
      light: '#ffca28',
      dark: '#ffa000',
    },
    error: {
      main: '#dc3545',
      light: '#ef5350',
      dark: '#c62828',
    },
    info: {
      main: '#17a2b8',
      light: '#03a9f4',
      dark: '#0288d1',
    },
  },
  typography: {
    fontFamily: [
      'Poppins',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      color: '#003b82',
      fontSize: '2.5rem',
      lineHeight: 1.2,
      marginBottom: '1.5rem',
    },
    h2: {
      fontWeight: 600,
      color: '#0056b3',
      fontSize: '2rem',
      lineHeight: 1.3,
      marginBottom: '1.25rem',
    },
    h3: {
      fontWeight: 600,
      color: '#0056b3',
      fontSize: '1.75rem',
      lineHeight: 1.3,
      marginBottom: '1rem',
    },
    h4: {
      fontWeight: 600,
      color: '#0056b3',
      fontSize: '1.5rem',
      lineHeight: 1.4,
      marginBottom: '0.75rem',
    },
    h5: {
      fontWeight: 600,
      color: '#0056b3',
      fontSize: '1.25rem',
      lineHeight: 1.4,
      marginBottom: '0.5rem',
    },
    h6: {
      fontWeight: 600,
      color: '#0056b3',
      fontSize: '1.1rem',
      lineHeight: 1.5,
      marginBottom: '0.5rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#2c3e50',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#546e7a',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 24px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(90deg, #0056b3 0%, #003b82 100%)',
          '&:hover': {
            background: 'linear-gradient(90deg, #004a99 0%, #00326d 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(90deg, #ffc72c 0%, #e6b327 100%)',
          color: '#000',
          '&:hover': {
            background: 'linear-gradient(90deg, #e6b327 0%, #cc9f23 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px 0 rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

const STAGE_MESSAGES: Record<string, string> = {
  queued: 'Uploading your deck...',
  extracting: 'Reading your deck...',
  analyzing: 'Scoring 11 categories and evaluating your pitch...',
  finalizing: 'Finalizing your report...',
};

const AnalyzePage: React.FC = () => {
  const { result, isLoading, progressStage, error, analyzeFile } = useAnalysis();
  const [showAnalysis, setShowAnalysis] = useState(true);

  const handleFileUpload = async (file: File, email: string, country: string, privacyConsent: boolean) => {
    try {
      setShowAnalysis(true);
      await analyzeFile(file, email, country, privacyConsent);
    } catch (err) {
      console.error('Error in file upload:', err);
      throw err;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Analyze Your Pitch Deck
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your deck and get professional feedback powered by AI
        </Typography>
      </Box>

      {showAnalysis && (
        <Paper elevation={0} sx={{ p: 4, border: `1px solid ${alpha('#000', 0.08)}`, borderRadius: 3 }}>
          <FileUpload onFileUpload={handleFileUpload} />
        </Paper>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 6 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {(progressStage && STAGE_MESSAGES[progressStage]) || 'Analyzing your pitch deck...'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This usually takes about a minute.
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 4 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Box sx={{ mt: 4 }}>
          <ResultDisplay />
        </Box>
      )}
    </Container>
  );
};

const App: React.FC = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/analyze');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Navigation />
      <MainLayout>
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<LandingPage onGetStarted={handleGetStarted} />} />
          <Route path="/analyze" element={<AnalyzePage />} />
          {/* Internal IA staff review — intentionally not linked from navigation */}
          <Route path="/internal" element={<InternalReview />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/sample-report" element={
            <Container maxWidth="md" sx={{ py: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }} className="no-print">
                <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                  Sample Report
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  This is what your analysis looks like — generated for a (fictional) startup.
                </Typography>
              </Box>
              <ResultDisplay result={SAMPLE_ANALYSIS} sample />
            </Container>
          } />
          <Route path="/how-it-works" element={
            <Container maxWidth="md" sx={{ py: 6 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                How It Works
              </Typography>
              <Typography variant="body1" paragraph>
                PitchGrades gives you investor-grade feedback on your pitch deck in about a minute.
                Here's what happens when you hit upload:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 3 }}>1. Upload your deck</Typography>
              <Typography variant="body1" paragraph>
                We accept PDF, PowerPoint (.pptx), and Word (.docx) files up to 100MB. PDFs get the
                deepest analysis — our AI reads every slide visually, so your design, charts, and
                data visualizations are evaluated alongside your content.
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 3 }}>2. AI analysis</Typography>
              <Typography variant="body1" paragraph>
                Your deck is scored against the Guy Kawasaki pitch model across 11 categories —
                from problem framing and value proposition to financials and the funding ask. Every
                score comes with specific written reasoning, not just a number. Your overall score
                is computed directly from the category scores, so the headline always matches the
                breakdown.
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 3 }}>3. Actionable results</Typography>
              <Typography variant="body1" paragraph>
                You get your key strengths, the weaknesses investors will notice, and concrete
                recommendations to improve — plus how your deck compares to others we've analyzed.
                Download the full report as a PDF and get to work.
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 3 }}>
                Curious what the output looks like? <Link to="/sample-report">View a sample report</Link>,
                or <Link to="/analyze">analyze your deck now</Link>.
              </Typography>
            </Container>
          } />
          <Route path="/contact" element={
            <Container maxWidth="md" sx={{ py: 6 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                PitchGrades is built by The International Accelerator, an Austin, TX-based program
                helping international founders enter and scale in the US market.
              </Typography>
              <Typography variant="body1" paragraph>
                Questions about your analysis, the program, or anything else — including privacy
                and data-deletion requests:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                <MuiLink href="mailto:ford@iaccelerator.com">ford@iaccelerator.com</MuiLink>
              </Typography>
            </Container>
          } />
        </Routes>
        </ErrorBoundary>
      </MainLayout>
    </ThemeProvider>
  );
};

const AppWithRouter: React.FC = () => (
  <Router>
    <AnalysisProvider>
      <App />
    </AnalysisProvider>
  </Router>
);

export default AppWithRouter;
